import type { AdminAppealDetailDto, ResolveAppealBody } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AppealContextGrid } from './_components/AppealContextGrid';
import { AppealDetailFooter } from './_components/AppealDetailFooter';
import { AppealDetailHeader } from './_components/AppealDetailHeader';
import { EvidenceGallery } from './_components/EvidenceGallery';
import { ResolveAppealDialog } from './_components/ResolveAppealDialog';
import { ResolvedDecisionBlock } from './_components/ResolvedDecisionBlock';
import { canResolve, canReviewStart, isResolved } from './_lib/appealActions';
import { useResolveAppeal } from './_lib/useResolveAppeal';
import { useReviewStart } from './_lib/useReviewStart';
import { useAdminAppealDetail } from '../../api/admin-appeals';

/**
 * Appeal detail drawer (AUT-130 / ADMIN-18).
 *
 * Right-side 720px drawer: header (AP-XXXX + status/priority badges) +
 * body (context grid with DUAL PII-gated identities + evidence gallery
 * + — when `resolved` — the read-only persisted decision) + footer.
 *
 * Footer (both actions are role + permission + audit gated server-side —
 * AUT-138 removed per-action TOTP step-up):
 *   - "Начать рассмотрение" (review_start) = PLAIN mutation; enabled only
 *     on `pending`.
 *   - "Вынести решение" (resolve) = terminal money-moving mutation; enabled
 *     while not `resolved`. Opens the outcome-first `ResolveAppealDialog`.
 *   - A `resolved` appeal renders read-only (no buttons; the persisted
 *     decision block surfaces the outcome).
 *
 * Both mutations invalidate the `['admin','appeals']` prefix so the list +
 * drawer refetch (NOT optimistic-patched — both action envelopes are the
 * user-facing `AppealDto`, a different shape from the detail).
 *
 * Evidence (AUT-132): the detail query mints short-lived (5-min) signed
 * GET URLs on `detail.evidence[]`; `EvidenceGallery` renders them. No
 * separate evidence/media fetch — the detail fetch IS the evidence read
 * path, and a 403 after expiry is recovered by re-opening the drawer.
 */
const DRAWER_WIDTH = 720;

const DrawerBody = observer(
  ({
    detail,
    onClose,
    onBusyChange,
  }: {
    detail: AdminAppealDetailDto;
    onClose: () => void;
    onBusyChange: (busy: boolean) => void;
  }): ReactElement => {
    const [resolveOpen, setResolveOpen] = useState(false);

    const reviewStart = useReviewStart();
    const resolve = useResolveAppeal();
    const busy = reviewStart.isPending || resolve.busy;
    const resolved = isResolved(detail.status);
    const orderTotal = Number(detail.order.pricingTotal);

    // Surface `busy` to the parent so a backdrop click can't dismiss the
    // drawer mid-action (consistent with the inner dialog close-gating).
    useEffect(() => {
      onBusyChange(busy);
    }, [busy, onBusyChange]);

    const onConfirmResolve = (body: ResolveAppealBody): void => {
      void resolve.run({ id: detail.id, body }).then((ok) => {
        if (ok) {
          setResolveOpen(false);
          onClose();
        }
      });
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
          }}
        >
          <AppealDetailHeader detail={detail} onClose={onClose} />
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: '22px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <AppealContextGrid detail={detail} />
            {resolved ? <ResolvedDecisionBlock detail={detail} /> : null}
            <EvidenceGallery detail={detail} />
          </Box>
          <AppealDetailFooter
            canReviewStart={canReviewStart(detail.status)}
            canResolve={canResolve(detail.status)}
            busy={busy}
            onReviewStart={() => reviewStart.mutate(detail.id)}
            onResolve={() => setResolveOpen(true)}
          />
        </Box>

        <ResolveAppealDialog
          open={resolveOpen}
          loading={busy}
          orderTotal={orderTotal}
          onConfirm={onConfirmResolve}
          onCancel={() => setResolveOpen(false)}
        />
      </>
    );
  },
);

interface AppealDetailDrawerProps {
  appealId: string | null;
  onClose: () => void;
}

export const AppealDetailDrawer = ({
  appealId,
  onClose,
}: AppealDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminAppealDetail(appealId);
  const [busy, setBusy] = useState(false);

  // While an action is in flight, a backdrop / Esc dismissal is a no-op.
  const handleClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  // Clear any lingering busy flag once the drawer is fully closed.
  useEffect(() => {
    if (appealId === null) setBusy(false);
  }, [appealId]);

  return (
    <Drawer anchor="right" open={appealId !== null} onClose={handleClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('appeals.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? (
        <DrawerBody
          key={query.data.id}
          detail={query.data}
          onClose={onClose}
          onBusyChange={setBusy}
        />
      ) : null}
    </Drawer>
  );
};
